import { mapIntegrationProviderFromDTO } from '@/modules/common/mappers/integration.mapper';

import {
  ProjectChangeSectionDTO,
  ProjectChangeSectionRequest,
} from '../types/dto/project-change-section.dto';
import { ProjectChangedFieldNameDTO } from '../types/dto/project-changed-field.dto';
import {
  ProjectChangedFieldResponse,
  ProjectChangeMetadataResponse,
} from '../types/dto/response/project-change-response';
import {
  ProjectChangedBy,
  ProjectChangedField,
  ProjectChangeLogEntry,
  ProjectChangeType,
} from '../types/project-changes';

/**
 * Maps project change section from DTO to domain model
 */
export const mapProjectChangeSectionFromDTO = (dto: ProjectChangeSectionDTO): ProjectChangeType => {
  switch (dto) {
    case ProjectChangeSectionDTO.INFO:
      return ProjectChangeType.Info;
    case ProjectChangeSectionDTO.BILLING:
      return ProjectChangeType.Billing;
    case ProjectChangeSectionDTO.INTEGRATIONS:
      return ProjectChangeType.Integration;
    default:
      return ProjectChangeType.Info;
  }
};

/**
 * Maps project change type to DTO for API requests
 */
export const mapProjectChangeTypeToDTO = (type: ProjectChangeType): ProjectChangeSectionRequest => {
  switch (type) {
    case ProjectChangeType.Info:
      return ProjectChangeSectionDTO.INFO;
    case ProjectChangeType.Billing:
      return ProjectChangeSectionDTO.BILLING;
    case ProjectChangeType.Integration:
      return ProjectChangeSectionDTO.INTEGRATIONS;
    default:
      return ProjectChangeSectionDTO.INFO;
  }
};

/**
 * Gets localized value - prefers valueKey over value
 */
const getLocalizedValue = (value: string | null, valueKey: string | null): string | null => {
  return valueKey || value;
};

/**
 * Maps a single field from DTO to domain model
 */
const mapSingleField = (field: ProjectChangedFieldResponse): ProjectChangedField => {
  return {
    field: field.fieldName,
    fieldLocalizedName: field.fieldLocalizedName,
    from: getLocalizedValue(field.oldValue, field.oldValueKey),
    to: getLocalizedValue(field.newValue, field.newValueKey),
    subfieldChanges: field.subfieldChanges ? mapChangedFields(field.subfieldChanges) : null,
  };
};

/**
 * Groups fields by fieldName and creates grouped entries
 */
const groupFields = (fields: ProjectChangedFieldResponse[]): ProjectChangedField[] => {
  const grouped = new Map<string, ProjectChangedFieldResponse[]>();
  const ungrouped: ProjectChangedFieldResponse[] = [];

  // Group fields with same fieldName that have subfieldChanges
  fields.forEach(field => {
    if (field.subfieldChanges && field.subfieldChanges.length > 0) {
      const existing = grouped.get(field.fieldName);
      if (existing) {
        existing.push(field);
      } else {
        grouped.set(field.fieldName, [field]);
      }
    } else {
      ungrouped.push(field);
    }
  });

  const result: ProjectChangedField[] = [];

  // Add grouped entries
  grouped.forEach((groupedFields, fieldName) => {
    if (groupedFields.length > 1) {
      // Multiple entries with same fieldName - create grouped field
      result.push({
        field: fieldName,
        fieldLocalizedName: null,
        from: null,
        to: null,
        isGrouped: true,
        groupCount: groupedFields.length,
        subfieldChanges: groupedFields.map(mapSingleField),
      });
    } else {
      // Single entry - add as regular field
      result.push(mapSingleField(groupedFields[0]));
    }
  });

  // Add ungrouped fields
  ungrouped.forEach(field => {
    result.push(mapSingleField(field));
  });

  return result;
};

/**
 * Sorts changed fields according to business rules
 * USER_RATES should always appear last
 */
const sortChangedFields = (fields: ProjectChangedField[]): ProjectChangedField[] => {
  return fields.sort((a, b) => {
    // USER_RATES should always be last
    if (a.field === ProjectChangedFieldNameDTO.USER_RATES) return 1;
    if (b.field === ProjectChangedFieldNameDTO.USER_RATES) return -1;
    return 0;
  });
};

/**
 * Maps changed fields from API response to domain model
 * Handles grouping of repeated fields (like USER_RATES) and sorting
 */
const mapChangedFields = (fields: ProjectChangedFieldResponse[]): ProjectChangedField[] => {
  const grouped = groupFields(fields);
  return sortChangedFields(grouped);
};

/**
 * Maps changed by information from API response to domain model
 */
const mapChangedBy = (response: ProjectChangeMetadataResponse): ProjectChangedBy => {
  if (response.changedByIntegration) {
    return {
      id: 0,
      name: mapIntegrationProviderFromDTO(response.changedByIntegration),
      profileImageUrl: null,
      isIntegration: true,
      integrationProvider: mapIntegrationProviderFromDTO(response.changedByIntegration),
    };
  }

  if (response.changedByUser) {
    return {
      id: response.changedByUser.id,
      name: response.changedByUser.name,
      profileImageUrl: response.changedByUser.profileImageUrl ?? null,
      isIntegration: false,
    };
  }

  // Fallback for unknown case
  return {
    id: 0,
    name: 'Unknown',
    profileImageUrl: null,
    isIntegration: false,
  };
};

/**
 * Maps project change from API response to domain model
 */
export const mapProjectChangeFromResponse = (
  response: ProjectChangeMetadataResponse
): ProjectChangeLogEntry => {
  return {
    id: response.id,
    type: mapProjectChangeSectionFromDTO(response.section),
    startDate: response.effectiveStartDate,
    endDate: response.effectiveEndDate,
    changedBy: mapChangedBy(response),
    changedFields: mapChangedFields(response.changedFields),
    allowedActions: response.allowedActions,
    isActive: response.isActive,
  };
};

/**
 * Maps multiple project changes from API response to domain model
 */
export const mapProjectChangesFromResponse = (
  responses: ProjectChangeMetadataResponse[]
): ProjectChangeLogEntry[] => {
  return responses.map(mapProjectChangeFromResponse);
};
